import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Apple-inspired easing
const appleEase: [number, number, number, number] = [0.22, 1, 0.36, 1];

export default function LandingHeader() {
  const ctaLink = '/role-select';
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <motion.header
      initial={{ opacity: 0, y: -16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: appleEase }}
      className="fixed top-0 left-0 right-0 z-50 bg-background/70 backdrop-blur-xl border-b border-border/30"
    >
      <div className="container mx-auto px-6 md:px-8 lg:px-12">
        <div className="flex justify-between items-center h-16 md:h-20">
          {/* Logo with hover effect */}
          <Link to="/" className="group flex items-center gap-2.5">
            <motion.div
              className="w-16 h-16 rounded-xl overflow-hidden transition-all duration-300 group-hover:scale-105 group-hover:shadow-md"
              whileHover={{ rotate: [0, -5, 5, 0], transition: { duration: 0.5 } }}
            >
              <img alt="UnlockEd Tutoring" width={64} height={64} className="w-full h-full object-cover" src="/lovable-uploads/589a960a-92ce-4127-9db4-cb59ef5dcf03.webp" />
            </motion.div>
            <span className="font-display font-bold text-2xl tracking-tight transition-colors duration-300 group-hover:text-primary">UnlockEd Tutoring</span>
          </Link>

          {/* Desktop navigation */}
          <nav className="hidden md:flex items-center gap-8" aria-label="Main navigation">
            <Link to="/privacy" className="relative text-sm text-muted-foreground hover:text-foreground transition-colors duration-300 after:absolute after:left-0 after:bottom-[-4px] after:h-[2px] after:w-0 after:bg-accent after:transition-all after:duration-300 hover:after:w-full">
              Privacy
            </Link>
            <Link to="/terms" className="relative text-sm text-muted-foreground hover:text-foreground transition-colors duration-300 after:absolute after:left-0 after:bottom-[-4px] after:h-[2px] after:w-0 after:bg-accent after:transition-all after:duration-300 hover:after:w-full">
              Terms
            </Link>
            <Link to={ctaLink}>
              <Button size="sm" variant="accent" className="rounded-full px-6 transition-all duration-300 hover:shadow-md hover:shadow-accent/25 hover:scale-[1.02] active:scale-[0.98]">
                Get Started
              </Button>
            </Link>
          </nav>

          {/* Mobile: hamburger + CTA */}
          <div className="md:hidden flex items-center gap-2">
            <Link to={ctaLink}>
              <Button size="sm" variant="accent" className="rounded-full px-4 transition-all duration-300 hover:shadow-md active:scale-[0.98]">
                Start
              </Button>
            </Link>
            <button
              onClick={() => setMobileOpen(prev => !prev)}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
              className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile dropdown menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: appleEase }}
            className="md:hidden overflow-hidden border-t border-border/30 bg-background/95 backdrop-blur-xl"
            aria-label="Mobile navigation"
          >
            <div className="container mx-auto px-6 py-4 flex flex-col gap-1">
              <Link
                to="/privacy"
                onClick={() => setMobileOpen(false)}
                className="text-sm text-muted-foreground hover:text-foreground hover:bg-muted px-3 py-3 rounded-lg transition-colors min-h-[44px] flex items-center"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                onClick={() => setMobileOpen(false)}
                className="text-sm text-muted-foreground hover:text-foreground hover:bg-muted px-3 py-3 rounded-lg transition-colors min-h-[44px] flex items-center"
              >
                Terms of Service
              </Link>
              <Link
                to="/cookies"
                onClick={() => setMobileOpen(false)}
                className="text-sm text-muted-foreground hover:text-foreground hover:bg-muted px-3 py-3 rounded-lg transition-colors min-h-[44px] flex items-center"
              >
                Cookie Preferences
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}